import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ChevronDown, ExternalLink, Menu, MessageCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { CONTACT, LOGO, SORTIMENT } from "@/content";

const navItems = [
  { to: "/", label: "Home", jp: "ホーム", testid: "nav-home-link" },
  { to: "/events", label: "Events", jp: "イベント", testid: "nav-events-link" },
  { to: "/ankauf", label: "Ankauf", jp: "買取", testid: "nav-ankauf-link" },
  { to: "/kontakt", label: "Kontakt", jp: "連絡", testid: "nav-kontakt-link" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 border-b border-border backdrop-blur-md transition-shadow ${
        scrolled ? "bg-background/85 shadow-lg shadow-black/40" : "bg-background/60"
      }`}
    >
      <div className="relative mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 md:h-[76px]">
        {/* Logo links */}
        <Link to="/" data-testid="header-logo-link" className="flex shrink-0 items-center">
          <img
            src={LOGO}
            alt="Shibuya Anime & TCG Store"
            className="h-10 w-auto md:h-12"
          />
        </Link>

        {/* Nav mittig (absolut zentriert) */}
        <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-9 md:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `link-slide group flex flex-col items-center text-sm font-medium transition-colors ${
                  isActive ? "active text-foreground" : "text-muted-foreground hover:text-foreground"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span>{item.label}</span>
                  <span
                    className={`font-jp text-[9px] leading-none tracking-widest transition-colors ${
                      isActive ? "text-primary" : "text-muted-foreground/50 group-hover:text-primary/70"
                    }`}
                  >
                    {item.jp}
                  </span>
                </>
              )}
            </NavLink>
          ))}
          {/* Sortiment Dropdown */}
          <div className="group relative">
            <button
              type="button"
              data-testid="nav-sortiment"
              className="link-slide flex flex-col items-center text-sm font-medium text-muted-foreground transition-colors hover:text-foreground group-focus-within:text-foreground"
            >
              <span className="flex items-center gap-1">
                Sortiment <ChevronDown className="h-3 w-3 opacity-60 transition-transform group-hover:rotate-180" />
              </span>
              <span className="font-jp text-[9px] leading-none tracking-widest text-muted-foreground/50 transition-colors group-hover:text-primary/70">
                商品
              </span>
            </button>
            <div className="invisible absolute left-1/2 top-full z-50 w-60 -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
              <div className="rounded-2xl border border-border bg-background/95 p-2 shadow-xl shadow-black/40 backdrop-blur-md">
                {SORTIMENT.map((s) => (
                  <Link
                    key={s.to}
                    to={s.to}
                    data-testid={`nav-sortiment-${s.to}`}
                    className="flex items-center justify-between rounded-xl px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-primary/10 hover:text-foreground"
                  >
                    {s.label}
                    <span className="font-jp text-[10px] text-muted-foreground/50">{s.jp}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
          <a
            href="https://platin-grading.de"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-grading-link"
            className="link-slide group flex flex-col items-center text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            <span className="flex items-center gap-1">
              Grading
              <ExternalLink className="h-3 w-3 opacity-60" />
            </span>
            <span className="font-jp text-[9px] leading-none tracking-widest text-muted-foreground/50 transition-colors group-hover:text-primary/70">
              鑑定
            </span>
          </a>
        </nav>

        {/* CTAs rechts – Anrufen primär */}
        <div className="hidden items-center gap-2.5 md:flex">
          <Button
            asChild
            data-testid="header-phone-button"
            className="rounded-xl bg-primary px-4 font-medium text-primary-foreground shadow-[0_10px_30px_hsl(22_100%_50%/0.3)] transition-shadow hover:shadow-[0_14px_40px_hsl(22_100%_50%/0.45)]"
          >
            <a href={CONTACT.phoneHref}>
              <Phone className="mr-1.5 h-4 w-4" />
              Anrufen
            </a>
          </Button>
          <Button
            asChild
            variant="outline"
            size="icon"
            data-testid="header-whatsapp-button"
            className="rounded-xl border-border bg-transparent hover:bg-primary/10"
          >
            <a
              href={CONTACT.whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp schreiben"
            >
              <MessageCircle className="h-4 w-4" />
            </a>
          </Button>
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild className="md:hidden">
            <Button
              variant="outline"
              size="icon"
              data-testid="mobile-menu-button"
              className="rounded-xl border-border bg-transparent"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-72 border-border bg-background">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SheetDescription className="sr-only">
              Hauptnavigation und Kontaktoptionen
            </SheetDescription>
            <img src={LOGO} alt="Shibuya Anime & TCG Store" className="mt-2 h-10 w-auto" />
            <div className="mt-8 flex flex-col gap-6">
              {navItems.map((item) => (
                <SheetClose asChild key={item.to}>
                  <NavLink
                    to={item.to}
                    data-testid={`mobile-${item.testid}`}
                    className={({ isActive }) =>
                      `flex items-baseline gap-3 font-display text-3xl uppercase tracking-wide ${
                        isActive ? "text-primary" : "text-foreground"
                      }`
                    }
                  >
                    {item.label}
                    <span className="font-jp text-xs text-muted-foreground">{item.jp}</span>
                  </NavLink>
                </SheetClose>
              ))}
              <a
                href="https://platin-grading.de"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-grading-link"
                className="flex items-baseline gap-3 font-display text-3xl uppercase tracking-wide text-foreground"
              >
                Grading
                <span className="flex items-center gap-1 font-jp text-xs text-muted-foreground">
                  鑑定 <ExternalLink className="h-3 w-3" />
                </span>
              </a>

              <div className="border-t border-border pt-5">
                <p className="font-mono-ui text-[11px] uppercase tracking-widest text-muted-foreground">Sortiment</p>
                <div className="mt-3 flex flex-col gap-3">
                  {SORTIMENT.map((s) => (
                    <SheetClose asChild key={s.to}>
                      <Link
                        to={s.to}
                        data-testid={`mobile-nav-sortiment-${s.to}`}
                        className="flex items-baseline gap-2 text-lg font-medium text-foreground"
                      >
                        {s.label}
                        <span className="font-jp text-[10px] text-muted-foreground">{s.jp}</span>
                      </Link>
                    </SheetClose>
                  ))}
                  <SheetClose asChild>
                    <Link to="/anfahrt" data-testid="mobile-nav-anfahrt-link" className="text-lg font-medium text-foreground">Anfahrt</Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link to="/ueber-uns" data-testid="mobile-nav-ueber-uns-link" className="text-lg font-medium text-foreground">Über uns</Link>
                  </SheetClose>
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-3">
                <Button
                  asChild
                  data-testid="mobile-phone-button"
                  className="h-12 rounded-xl bg-primary font-medium text-primary-foreground"
                >
                  <a href={CONTACT.phoneHref}>
                    <Phone className="mr-2 h-4 w-4" /> Anrufen: {CONTACT.phoneDisplay}
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  data-testid="mobile-whatsapp-button"
                  className="h-12 rounded-xl border-border bg-transparent"
                >
                  <a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp schreiben
                  </a>
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
